import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Plus, ArrowRight, Globe, Check, AlertTriangle, Gauge, Image as ImageIcon, RotateCcw } from "lucide-react";
import { PageHeader, Card, Spinner, Empty } from "@/components/common";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../../components/ui/dialog";
import { studioApi, useStudio, newKey, loadStartDraft, saveStartDraft, clearStartDraft } from "./studioApi";

const PHASE_COLOR = "bg-[#A3E635]/15 text-[#A3E635] border-[#A3E635]/30";
const cls = "w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#A3E635]";
const Field = ({ t, hint, children }) => {
  const id = children.props["data-testid"] || children.props.id;
  const control = id ? React.cloneElement(children, { id }) : children;
  return (
    <div>
      <label htmlFor={id} className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t}</label>
      <div className="mt-1">{control}</div>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
};

function StartDialog({ open, onClose, onCreated, triggerRef }) {
  const { t, err } = useStudio();
  const urlInput = useRef(null);
  const savingRef = useRef(false);
  const keyRef = useRef(newKey());
  const [company, setCompany] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [draftOid, setDraftOid] = useState(null);
  const [existingOrder, setExistingOrder] = useState(null); // tatsächlicher Stand eines bereits angelegten Auftrags (Logo/Revision)
  const [checkingResume, setCheckingResume] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [choice, setChoice] = useState(null);

  // Begonnenen Versuch bis zum bestätigten Ergebnis erhalten: beim Öffnen wird ein
  // gespeicherter, unvollendeter Versuch (gleicher Schlüssel, ggf. bereits angelegter
  // Auftrag) übernommen, statt ihn stillschweigend durch einen neuen zu ersetzen. Bei
  // bereits bekanntem Auftrag wird der TATSÄCHLICHE Stand geladen (Logo/Revision) –
  // eine verlorene Uploadantwort darf nie zu einem dauerhaft scheiternden Retry führen.
  useEffect(() => {
    if (!open) return;
    const draft = loadStartDraft();
    setLogoFile(null); setError(null); setCandidates(null); setChoice(null); setExistingOrder(null);
    if (draft?.idempotency_key) {
      keyRef.current = draft.idempotency_key;
      setCompany(draft.company || "");
      setWebsiteUrl(draft.website_url || "");
      setDraftOid(draft.oid || null);
      setResumed(true);
      if (draft.oid) {
        setCheckingResume(true);
        studioApi.order(draft.oid).then((r) => setExistingOrder(r.data.order))
          .catch(() => setExistingOrder(null))
          .finally(() => setCheckingResume(false));
      }
    } else {
      keyRef.current = newKey();
      setCompany(""); setWebsiteUrl(""); setDraftOid(null);
      setResumed(false);
    }
  }, [open]);

  // Eingaben laufend sichern, damit ein Abbruch durch Neuladen/Netzwerkfehler denselben
  // Versuch (denselben Schlüssel) fortsetzen kann, statt einen Duplikatauftrag zu erzeugen.
  useEffect(() => {
    if (!open) return;
    if (!company.trim() && !websiteUrl.trim() && !draftOid) return;
    saveStartDraft({ company, website_url: websiteUrl, idempotency_key: keyRef.current, oid: draftOid });
  }, [open, company, websiteUrl, draftOid]);

  const close = () => { if (savingRef.current) return; onClose(); };

  const startFresh = () => {
    clearStartDraft();
    keyRef.current = newKey();
    setCompany(""); setWebsiteUrl(""); setLogoFile(null); setDraftOid(null); setExistingOrder(null);
    setResumed(false); setError(null); setCandidates(null); setChoice(null);
  };

  const doStart = async (confirmRef) => {
    const body = { website_url: websiteUrl.trim(), idempotency_key: keyRef.current };
    if (company.trim()) body.company = company.trim();
    if (confirmRef) body.confirm_company_ref = confirmRef;
    const r = await studioApi.start(body);
    return { order: r.data.order, nameConfirmed: r.data.name_confirmed };
  };

  const submit = async () => {
    if (savingRef.current) return;
    savingRef.current = true; setSaving(true); setError(null);
    let currentOid = draftOid; // lokal statt State: innerhalb dieses Aufrufs stets der tatsächliche Stand, nie der veraltete Schluss der React-State-Closure.
    try {
      let order;
      let nameConfirmed = true;
      if (!currentOid) {
        try {
          ({ order, nameConfirmed } = await doStart(choice));
        } catch (e) {
          const detail = e?.response?.data?.detail;
          if (e?.response?.status === 409 && typeof detail === "string" && detail.includes("/match")) {
            const m = await studioApi.match({ website_url: websiteUrl.trim() });
            if (m.data.status === "ambiguous") {
              setCandidates(m.data.candidates);
              return; // Martin muss zuerst bestätigen – kein automatischer Abschluss mit leerer Auswahl.
            }
            // "unique"/"new": eindeutig auflösbar, kein Abbruch mit festhängender leerer Auswahl.
            const autoRef = m.data.status === "unique" ? { type: m.data.match.type, id: m.data.match.id } : null;
            ({ order, nameConfirmed } = await doStart(autoRef));
          } else {
            throw e;
          }
        }
        currentOid = order.id;
        setDraftOid(currentOid);
        saveStartDraft({ company, website_url: websiteUrl, idempotency_key: keyRef.current, oid: currentOid });
      } else {
        // Fortsetzung eines bereits angelegten Auftrags: tatsächlichen aktuellen Stand
        // (Logo, Revision) laden statt von einer veralteten/erfundenen Revision auszugehen.
        const detail = await studioApi.order(currentOid);
        order = detail.data.order;
      }
      if (!order.logo) {
        await studioApi.uploadLogo(currentOid, order.revision, logoFile);
      }
      clearStartDraft();
      toast.success(t("Studio-Vorgang gestartet"));
      if (!nameConfirmed) toast(t("Firmenname aus der Domain abgeleitet – bitte im Auftrag bestätigen."));
      studioApi.scanSource(currentOid).catch(() => {}); // sicherer, kostenloser Originalabruf – darf den Start nie blockieren.
      onCreated(currentOid);
    } catch (e) {
      setError(err(e, currentOid ? "Auftrag ist gespeichert, aber das Logo fehlt noch oder der Upload ist fehlgeschlagen – bitte erneut versuchen." : "Start fehlgeschlagen."));
    } finally { savingRef.current = false; setSaving(false); }
  };

  const logoAlreadyUploaded = !!(draftOid && existingOrder?.logo);
  const errs = [];
  if (!websiteUrl.trim()) errs.push(t("WWW-Link fehlt."));
  if (!logoFile && !logoAlreadyUploaded) errs.push(t("Firmenlogo fehlt."));

  if (!open) return null;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="studio-start-modal" closeLabel={t("Schliessen")} closeDisabled={saving}
        className="w-[calc(100vw_-_2rem)] max-w-lg max-h-[90dvh] overflow-y-auto min-w-0 bg-[#121217] border-white/10 text-white rounded-2xl p-4 sm:p-6"
        onOpenAutoFocus={(e) => { e.preventDefault(); if (!draftOid) urlInput.current?.focus(); }}
        onCloseAutoFocus={(e) => { e.preventDefault(); triggerRef.current?.focus(); }}
        onEscapeKeyDown={(e) => { if (savingRef.current) e.preventDefault(); }}
        onInteractOutside={(e) => { if (savingRef.current) e.preventDefault(); }}>
        <div className="pr-8">
          <DialogTitle className="text-lg font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#A3E635]" /> {t("Studio starten")}</DialogTitle>
          <DialogDescription className="text-sm text-gray-400 mt-1">{t("Firmenlogo und WWW-Link eingeben. Danach wird die öffentliche Originalseite automatisch abgerufen – alles Weitere zeigt der Auftrag mit dem jeweils wartenden Schritt.")}</DialogDescription>
        </div>
        {resumed && (
          <div className="mt-3 flex items-center justify-between gap-3 bg-amber-400/10 border border-amber-400/25 rounded-lg px-3 py-2" data-testid="studio-start-resumed-banner">
            <p className="text-xs text-amber-200">
              {!draftOid ? t("Unvollendeter Versuch gefunden – Eingaben übernommen.")
                : checkingResume ? t("Wird geprüft…")
                : logoAlreadyUploaded ? t("Auftrag bereits vollständig angelegt (inkl. Logo) – kann direkt geöffnet werden.")
                : t("Auftrag ist bereits angelegt, Logo fehlt noch oder der Uploadversuch ist fehlgeschlagen – bitte erneut auswählen.")}
            </p>
            <button onClick={startFresh} data-testid="studio-start-fresh" className="text-xs text-gray-300 hover:text-white inline-flex items-center gap-1 shrink-0"><RotateCcw className="w-3.5 h-3.5" /> {t("Neu beginnen")}</button>
          </div>
        )}
        <fieldset disabled={saving} className="space-y-3 mt-4">
          <fieldset disabled={!!draftOid} className="contents">
            <Field t={t("WWW-Link *")}><input ref={urlInput} value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} data-testid="studio-start-url" className={cls} placeholder="www.muster.ch" /></Field>
            <Field t={t("Firmenname (optional)")} hint={t("Leer lassen: bei bestehendem Datensatz wird der echte Name übernommen, sonst aus der Domain abgeleitet und als unbestätigt markiert.")}>
              <input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="studio-start-company" className={cls} placeholder={t("optional, z. B. Muster AG")} />
            </Field>
          </fieldset>
          {!logoAlreadyUploaded && (
            <Field t={t("Firmenlogo *")} hint={t("PNG, JPG, WEBP oder SVG, max. 2 MB.")}>
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="studio-start-logo" className={`${cls} py-2 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-[#A3E635] file:text-black file:text-xs file:font-bold`} />
            </Field>
          )}
          {logoFile && <p className="text-xs text-gray-400 flex items-center gap-1.5" data-testid="studio-start-logo-name"><ImageIcon className="w-3.5 h-3.5" /> {logoFile.name}</p>}
        </fieldset>
        {candidates && candidates.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="studio-start-candidates">
            <p className="text-sm text-amber-200 flex items-center gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> {t("Mehrdeutig – bitte bestehende Firma bestätigen, bevor gestartet wird.")}</p>
            {candidates.map((c) => (
              <label key={`${c.type}-${c.id}`} className="flex items-center gap-2 text-sm bg-white/5 rounded-lg px-3 py-2 cursor-pointer" data-testid={`studio-start-candidate-${c.id}`}>
                <input type="radio" name="studio-company-ref" className="accent-[#A3E635]" checked={choice?.id === c.id} onChange={() => setChoice({ type: c.type, id: c.id })} />
                <span>{c.label}</span><span className="text-xs text-gray-500">({c.type === "client" ? t("Kunde") : t("Interessent")})</span>
              </label>
            ))}
          </div>
        )}
        {error && <div className="mt-4 text-sm text-red-300 flex items-center gap-2" data-testid="studio-start-error"><AlertTriangle className="w-4 h-4 shrink-0" /> {error}</div>}
        {!candidates && errs.length > 0 && <div className="mt-4 space-y-1" data-testid="studio-start-warnings">{errs.map((e, i) => <p key={i} className="text-sm text-red-300">{e}</p>)}</div>}
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={close} disabled={saving} data-testid="studio-start-cancel" className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-40">{t("Abbrechen")}</button>
          {candidates && candidates.length > 0 ? (
            <button onClick={submit} disabled={saving || !choice || errs.length > 0} data-testid="studio-start-confirm" className="bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 text-black font-bold py-2 px-5 rounded-lg inline-flex items-center gap-2"><Check className="w-4 h-4" /> {t("Bestätigen und starten")}</button>
          ) : (
            <button onClick={submit} disabled={saving || checkingResume || errs.length > 0} data-testid="studio-start-submit" className="bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 text-black font-bold py-2 px-5 rounded-lg inline-flex items-center gap-2"><Check className="w-4 h-4" /> {logoAlreadyUploaded ? t("Gespeicherten Auftrag öffnen") : draftOid ? t("Logo erneut hochladen") : t("Starten")}</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function StudioList() {
  const { t, fmtDate } = useStudio();
  const navigate = useNavigate();
  const startBtnRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [showStart, setShowStart] = useState(false);

  const load = () => studioApi.orders().then((r) => { setRows(r.data); setError(null); }).catch((e) => setError(e));
  useEffect(() => { load(); }, []);

  return (
    <div className="fade-in" data-testid="studio-list-page">
      <PageHeader title={t("Studio")} subtitle={t("Erster durchgehender Produktionsabschnitt: Firma starten, Recherche, Verbesserungsbrief, Leitstand.")}>
        <div className="flex gap-2">
          <Link to="/admin/studio/leitstand" className="border border-white/10 bg-white/[0.03] text-gray-200 rounded-lg px-4 py-2.5 text-sm hover:bg-white/[0.08] inline-flex items-center gap-2" data-testid="studio-leitstand-link"><Gauge className="w-4 h-4" /> {t("Leitstand")}</Link>
          <button ref={startBtnRef} onClick={() => setShowStart(true)} data-testid="studio-start-btn" className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg inline-flex items-center gap-2"><Plus className="w-4 h-4" /> {t("Studio starten")}</button>
        </div>
      </PageHeader>

      {error && <Card className="p-5 mb-5 border-red-400/25" data-testid="studio-list-error"><p role="alert" className="text-sm text-red-200">{t("Vorgänge konnten nicht geladen werden.")}</p></Card>}
      {!rows && !error ? <Spinner /> : rows && rows.length === 0 ? <Empty text={t("Noch kein Studio-Vorgang gestartet.")} /> : rows && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="studio-order-cards">
          {rows.map(({ order, studio_state }) => (
            <Card key={order.id} className="p-5 flex flex-col min-w-0" data-testid={`studio-order-card-${order.id}`}>
              <span className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold self-start ${PHASE_COLOR}`}>{studio_state.phase_label || studio_state.phase}</span>
              <h3 className="font-bold mt-2 break-words">{order.company}</h3>
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1 min-w-0"><Globe className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{order.website_url}</span></div>
              <p className="text-xs text-gray-500 mt-2">{t("Aktualisiert")} {fmtDate(studio_state.updated_at)}</p>
              <Link to={`/admin/studio/${order.id}`} className="mt-auto pt-3 border-t border-white/10 text-sm font-semibold text-[#A3E635] inline-flex items-center gap-2" data-testid={`studio-order-open-${order.id}`}>{t("Öffnen")} <ArrowRight className="w-4 h-4" /></Link>
            </Card>
          ))}
        </div>
      )}

      <StartDialog open={showStart} onClose={() => setShowStart(false)} triggerRef={startBtnRef}
        onCreated={(id) => { setShowStart(false); navigate(`/admin/studio/${id}`); }} />
    </div>
  );
}
